"use client";

import React, { useState } from "react";
import {
  Mail,
  Copy,
  Check,
  Send,
  MessageSquare,
  Sparkles,
  ShieldCheck,
  Clock,
  Compass,
  FileQuestion,
  Lightbulb,
  ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { SITE_CONFIG } from "@/config/site";

const INQUIRY_TOPICS = [
  { id: "suggestion", label: "🗡️ Generator Suggestion", desc: "Request a new universe, lore realm, or naming convention" },
  { id: "lore", label: "📜 Lore / Etymology Contribution", desc: "Suggest linguistic corrections or mythic depth" },
  { id: "partnership", label: "🤝 Partnership & Licensing", desc: "Collaborations, author tools, and tabletop integrations" },
  { id: "bug", label: "🛠️ Bug Report / Technical Feedback", desc: "Report an issue with the website or generator engine" },
  { id: "general", label: "💬 General Question for Paul Arcane", desc: "Worldbuilding questions or storyteller chat" },
];

export default function ContactFormClient() {
  const [copied, setCopied] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState("suggestion");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [status, setStatus] = useState<"idle" | "submitting" | "success">("idle");

  const emailAddress = SITE_CONFIG.email || "info@namearcane.com";

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(emailAddress);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("submitting");

    // Construct mailto link with pre-filled subject and body
    const topicLabel = INQUIRY_TOPICS.find((t) => t.id === selectedTopic)?.label || "General Inquiry";
    const subjectLine = encodeURIComponent(`[Name Arcane] ${topicLabel} - ${formData.subject || formData.name}`);
    const bodyContent = encodeURIComponent(
      `From: ${formData.name} (${formData.email})\nTopic: ${topicLabel}\n\nMessage:\n${formData.message}`
    );

    setTimeout(() => {
      setStatus("success");
      // Trigger user's mail client directly
      window.location.href = `mailto:${emailAddress}?subject=${subjectLine}&body=${bodyContent}`;
    }, 600);
  };

  return (
    <div className="space-y-12">
      {/* Top Direct Channel Highlight Card */}
      <div className="rounded-3xl border border-primary/30 bg-gradient-to-r from-card via-primary/5 to-card p-6 sm:p-10 shadow-xl relative overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
          <div className="md:col-span-8 space-y-2">
            <div className="flex items-center gap-2">
              <Badge variant="fantasy" className="text-[11px] uppercase tracking-wider">
                <Mail className="w-3.5 h-3.5 mr-1" />
                Direct Email Dispatch
              </Badge>
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <Clock className="w-3.5 h-3.5 text-emerald-400" />
                Replies within 24–48 hours
              </span>
            </div>
            <h2 className="text-xl sm:text-2xl font-bold font-fantasy text-foreground">
              Direct Contact: <span className="text-primary font-mono select-all">{emailAddress}</span>
            </h2>
            <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
              Reach Paul Arcane and the Name Arcane editorial team directly. We personally review every generator request, lore suggestion, and creative collaboration.
            </p>
          </div>

          <div className="md:col-span-4 flex flex-col sm:flex-row md:flex-col gap-3 justify-end">
            <Button
              onClick={handleCopyEmail}
              variant={copied ? "default" : "secondary"}
              className="w-full justify-center rounded-xl shadow-sm transition-all"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 mr-2 text-emerald-400" />
                  <span>Email Copied!</span>
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  <span>Copy {emailAddress}</span>
                </>
              )}
            </Button>
            <Button asChild className="w-full justify-center rounded-xl shadow-md">
              <a href={`mailto:${emailAddress}`}>
                <ExternalLink className="w-4 h-4 mr-2" />
                <span>Open Mail Client</span>
              </a>
            </Button>
          </div>
        </div>
      </div>

      {/* Main Interactive Form & Inquiry Channels */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
        {/* Left Column: Form */}
        <div className="lg:col-span-7">
          <div className="rounded-3xl border border-border/80 bg-card p-6 sm:p-10 shadow-xl">
            <div className="flex items-center gap-2 mb-6">
              <MessageSquare className="w-5 h-5 text-primary" />
              <h3 className="text-xl font-bold font-fantasy text-foreground">
                Send a Message
              </h3>
            </div>

            {status === "success" ? (
              <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-6 sm:p-8 text-center space-y-4">
                <div className="w-12 h-12 rounded-full bg-emerald-500/20 text-emerald-400 mx-auto flex items-center justify-center">
                  <Check className="w-6 h-6" />
                </div>
                <h4 className="text-lg font-bold text-foreground font-fantasy">
                  Message Dispatched!
                </h4>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed max-w-md mx-auto">
                  Your mail application has been opened with your inquiry prepared. If your mail client didn&apos;t open automatically, you can always write directly to <strong className="text-foreground">{emailAddress}</strong>.
                </p>
                <Button
                  onClick={() => {
                    setStatus("idle");
                    setFormData({ name: "", email: "", subject: "", message: "" });
                  }}
                  variant="outline"
                  size="sm"
                  className="rounded-xl mt-2"
                >
                  Send Another Note
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Topic Selector */}
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    Inquiry Topic
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {INQUIRY_TOPICS.map((topic) => (
                      <button
                        type="button"
                        key={topic.id}
                        onClick={() => setSelectedTopic(topic.id)}
                        className={`text-left p-3 rounded-xl border transition-all text-xs ${
                          selectedTopic === topic.id
                            ? "border-primary bg-primary/10 text-foreground shadow-sm ring-1 ring-primary/40"
                            : "border-border/60 bg-background/60 hover:bg-accent text-muted-foreground"
                        }`}
                      >
                        <div className="font-semibold text-foreground">{topic.label}</div>
                        <div className="text-[10px] text-muted-foreground/80 mt-0.5 line-clamp-1">
                          {topic.desc}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Name & Email */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label htmlFor="contact-name" className="text-xs font-semibold text-foreground">
                      Your Name / Handle
                    </label>
                    <Input
                      id="contact-name"
                      required
                      placeholder="e.g. Roland of Gilead"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="rounded-xl bg-background/80"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label htmlFor="contact-email" className="text-xs font-semibold text-foreground">
                      Your Email Address
                    </label>
                    <Input
                      id="contact-email"
                      type="email"
                      required
                      placeholder="name@domain.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="rounded-xl bg-background/80"
                    />
                  </div>
                </div>

                {/* Subject */}
                <div className="space-y-1.5">
                  <label htmlFor="contact-subject" className="text-xs font-semibold text-foreground">
                    Subject / Generator Request
                  </label>
                  <Input
                    id="contact-subject"
                    required
                    placeholder="e.g. Suggestion: Add High Elven Noble Houses Generator"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    className="rounded-xl bg-background/80"
                  />
                </div>

                {/* Message Body */}
                <div className="space-y-1.5">
                  <label htmlFor="contact-message" className="text-xs font-semibold text-foreground">
                    Message Details
                  </label>
                  <textarea
                    id="contact-message"
                    required
                    rows={5}
                    placeholder="Tell us what you'd like to see, describe the naming style or share your worldbuilding feedback..."
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full rounded-xl border border-input bg-background/80 px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-y"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={status === "submitting"}
                  size="lg"
                  className="w-full rounded-xl shadow-lg font-bold"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {status === "submitting" ? "Preparing Dispatch..." : "Send Message to Paul Arcane"}
                </Button>

                <p className="text-[11px] text-center text-muted-foreground/80 flex items-center justify-center gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5 text-primary" />
                  We respect your privacy. No spam or newsletters—only personal replies.
                </p>
              </form>
            )}
          </div>
        </div>

        {/* Right Column: FAQ & Channels */}
        <div className="lg:col-span-5 space-y-6">
          {/* Quick FAQ Card */}
          <div className="rounded-3xl border border-border/80 bg-card/70 p-6 sm:p-8 shadow-xl space-y-4">
            <div className="flex items-center gap-2">
              <FileQuestion className="w-4 h-4 text-primary" />
              <h3 className="text-base font-bold font-fantasy text-foreground">
                Frequently Asked Questions
              </h3>
            </div>

            <div className="space-y-4 text-xs">
              <div className="border-b border-border/60 pb-3">
                <h4 className="font-semibold text-foreground mb-1">
                  Can I use generated names commercially in my novel or RPG?
                </h4>
                <p className="text-muted-foreground leading-relaxed">
                  Yes! All names generated on Name Arcane are 100% royalty-free and open for use in published novels, indie video games, D&amp;D campaigns, and tabletop manuals.
                </p>
              </div>

              <div className="border-b border-border/60 pb-3">
                <h4 className="font-semibold text-foreground mb-1">
                  How do I request a new naming generator?
                </h4>
                <p className="text-muted-foreground leading-relaxed">
                  Use the contact form selecting <em>&ldquo;Generator Suggestion&rdquo;</em> or email us at <strong className="text-foreground">{emailAddress}</strong>. We add new fantasy universes and historical cultures every month.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-foreground mb-1">
                  Who curates the naming lore and etymologies?
                </h4>
                <p className="text-muted-foreground leading-relaxed">
                  Every category is designed by author <strong>Paul Arcane</strong> alongside historical onomastic resources and fantasy lore frameworks.
                </p>
              </div>
            </div>
          </div>

          {/* Generator Suggestion Tip Box */}
          <div className="rounded-3xl border border-primary/20 bg-primary/5 p-6 sm:p-8 space-y-3">
            <div className="flex items-center gap-2 text-primary font-bold text-xs uppercase tracking-wider font-fantasy">
              <Lightbulb className="w-4 h-4" />
              <span>Worldbuilder Collaboration</span>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Are you an indie game developer, tabletop designer, or fantasy author looking for custom naming convention tools for your lore? Let us know—we love building specialized naming modules for creative projects!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
